// src/components/modals/EditUserModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, User, Building, Save, LoaderCircle, Shield } from 'lucide-react';

// 1. RECEBER currentUser COMO PROP
export default function EditUserModal({ user, currentUser, onClose, onSave }) {
  const [formData, setFormData] = useState({
    nome: '',
    sobrenome: '',
    hub: '',
    role: '',
  });
  const [initialData, setInitialData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      const userData = {
        nome: user.nome || '',
        sobrenome: user.sobrenome || '',
        hub: user.hub || '',
        role: user.role || 'operator',
      };
      setFormData(userData);
      setInitialData(userData);
    }
  }, [user]);

  if (!user) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialData);
    if (hasChanged && !isLoading) {
      if (window.confirm("Você tem alterações não salvas. Deseja realmente sair?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.nome.trim()) {
      setError('O campo "Nome" é obrigatório.');
      return;
    }

    setIsLoading(true);
    try {
      // 2. ENVIAR TOKEN DE AUTENTICAÇÃO
      const token = sessionStorage.getItem('app_token');
      await axios.put(
        `/api/users/${user.id}`, 
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      onSave();
    } catch (err) {
      console.error("Erro ao atualizar usuário:", err);
      setError(err.response?.data?.detail || 'Não foi possível salvar as alterações.');
    } finally {
      setIsLoading(false);
    }
  };

  // 3. LÓGICA DE UI PARA DESABILITAR O CAMPO DE ROLE
  const isRoleSelectDisabled = 
    // Regra 1: admin-op não pode alterar a role de um admin-dev (o botão de editar já estaria desabilitado, mas é uma segurança extra)
    (currentUser.role === 'admin-op' && user.role === 'admin-dev') ||
    // Regra 2: Um admin-dev não pode rebaixar a si mesmo
    (currentUser.id === user.id && currentUser.role === 'admin-dev');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Editar Usuário</h2>
          <button onClick={handleClose} disabled={isLoading} className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
            <X size={20} className="text-gray-600" />
          </button>
        </div>
        
        <div className="mb-4 text-sm text-gray-600">
            <p><strong>ID:</strong> {user.id}</p>
            <p><strong>Email:</strong> {user.email}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="relative">
              <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" name="nome" value={formData.nome} onChange={handleChange} placeholder="Nome" disabled={isLoading} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EE4D2D]" />
            </div>
            <div className="relative">
              <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" name="sobrenome" value={formData.sobrenome} onChange={handleChange} placeholder="Sobrenome" disabled={isLoading} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EE4D2D]" />
            </div>
            <div className="relative">
              <Building size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" name="hub" value={formData.hub} onChange={handleChange} placeholder="Hub" disabled={isLoading} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EE4D2D]" />
            </div>
            <div className="relative">
              <Shield size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select 
                name="role" 
                value={formData.role} 
                onChange={handleChange} 
                disabled={isLoading || isRoleSelectDisabled} 
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EE4D2D] bg-white appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {/* 4. LÓGICA PARA EXIBIR OPÇÕES DE ROLE */}
                {/* Um admin-op não pode promover ninguém para admin-dev */}
                {currentUser.role === 'admin-dev' && <option value="admin-dev">Admin (Master)</option>}
                <option value="admin-op">Admin (Operações)</option>
                <option value="operator">Operador</option>
              </select>
            </div>
          </div>
          
          {error && <p className="text-red-500 text-sm mt-4">{error}</p>}

          <div className="mt-6 flex items-center justify-end space-x-3">
              <button type="button" onClick={handleClose} disabled={isLoading} className="px-6 py-2 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200">
                  Fechar
              </button>
              <button type="submit" disabled={isLoading} className="px-6 py-2 flex items-center justify-center bg-[#EE4D2D] text-white font-bold rounded-lg hover:bg-[#D03F2A] disabled:bg-red-300">
                  {isLoading ? <LoaderCircle size={20} className="animate-spin" /> : <><Save size={18} className="mr-2" /> Salvar</>}
              </button>
          </div>
        </form>
      </div>
    </div>
  );
}